/**
 * Boot never waits for the audio cache's whole-store id walk (job #804).
 *
 * `audioCache.ready()` walks every row of the IndexedDB audio store. On Tom's
 * iPhone, holding six downloaded courses, that took 80-140 s on 2026-09-23,
 * even reading keys only, and boot awaited it before anything else, so the play
 * button span for a minute or two on every fresh open.
 *
 * The walk now always runs in the background. The one thing that must be right
 * before the first sound is the fail-closed OFFLINE gate, which trusts
 * `persistent.has()` for the cycles about to play, and only when offline
 * playback is active (Offline Mode on, or offline at launch). For those
 * learners boot primes exactly the head rounds' clips with primary-key
 * lookups, bounded by a budget: that is a few hundred point reads, not the
 * whole store. By the time the learner is past the head rounds the walk has
 * long finished. Everything that computes "what is missing" to DOWNLOAD waits
 * for the full walk itself, off the boot path.
 */
export async function primeOfflineHead(
  cache: { primeIds(ids: Iterable<string>): Promise<void> },
  headIds: () => Iterable<string>,
  opts: { offlineGate: boolean; budgetMs: number },
): Promise<void> {
  if (!opts.offlineGate) return
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    await Promise.race([
      cache.primeIds(headIds()).catch(() => { /* fails open: the gate skips what it can't see */ }),
      new Promise<void>((resolve) => { timer = setTimeout(resolve, opts.budgetMs) }),
    ])
  } finally {
    if (timer) clearTimeout(timer)
  }
}
